import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Code as CodeIcon,
  Visibility as PreviewIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  FormatListBulleted as TocIcon,
} from '@mui/icons-material';
// @ts-ignore
import { marked } from 'marked';
// @ts-ignore
import DOMPurify from 'dompurify';
// Removed react-syntax-highlighter since marked handles code blocks
import { ViewerError } from '../../../types/viewer.types';
import { useDirectContent } from '../../../hooks/useDirectContent';

interface MarkdownViewerProps {
  contentId: string;
  metadata: {
    fileName: string;
    mimeType: string;
    fileSize: number;
    contentType: string;
  };
  onError?: (error: ViewerError) => void;
  onLoad?: () => void;
  theme?: 'light' | 'dark';
  className?: string;
  style?: React.CSSProperties;
}

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  contentId,
  metadata,
  onError,
  onLoad,
  theme = 'light',
  className,
  style,
}) => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'preview' | 'source' | 'split'>('preview');
  const [showToc, setShowToc] = useState<boolean>(false);
  const [tableOfContents, setTableOfContents] = useState<TableOfContentsItem[]>([]);
  const [copied, setCopied] = useState<boolean>(false);

  const { contentUrl, isDirectDownload, metadata: streamingMetadata } = useDirectContent(contentId, undefined, metadata as any);

  // Configure marked options
  useEffect(() => {
    // Set up marked with custom renderer
    marked.setOptions({
      gfm: true, // GitHub Flavored Markdown
      breaks: true, // Convert \n to <br>
      pedantic: false,
    });

    // Custom renderer for code blocks with syntax highlighting
    const renderer = new marked.Renderer();
    
    // Override code block rendering
    renderer.code = function({ text, lang, escaped }) {
      if (lang) {
        try {
          const highlighted = `<pre><code class="language-${lang}">${text}</code></pre>`;
          return highlighted;
        } catch (e) {
          console.error('Syntax highlighting error:', e);
        }
      }
      return `<pre><code>${text}</code></pre>`;
    };

    // Override heading rendering to extract TOC
    renderer.heading = function({ tokens, depth }) {
      const text = this.parser.parseInline(tokens);
      const id = text.toLowerCase().replace(/[^\w]+/g, '-');
      return `<h${depth} id="${id}">${text}</h${depth}>`;
    };

    marked.use({ renderer });
  }, []);

  // Load markdown content
  useEffect(() => {
    const loadMarkdown = async () => {
      if (!contentUrl) return;

      setLoading(true);
      try {
        console.log('Loading markdown content from:', contentUrl);
        const response = await fetch(contentUrl);
        const text = await response.text();
        setMarkdownContent(text);

        // Extract table of contents
        const headings: TableOfContentsItem[] = [];
        const lines = text.split('\n');
        lines.forEach((line) => {
          const match = line.match(/^(#{1,6})\s+(.+)/);
          if (match) {
            const level = match[1].length;
            const text = match[2];
            const id = text.toLowerCase().replace(/[^\w]+/g, '-');
            headings.push({ id, text, level });
          }
        });
        setTableOfContents(headings);

        onLoad?.();
      } catch (error: any) {
        console.error('Failed to load markdown:', error);
        const viewerError: ViewerError = {
          code: 'STREAMING_ERROR',
          message: 'Failed to load markdown content',
          details: error,
          retryable: true,
        };
        onError?.(viewerError);
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, [contentUrl, onLoad, onError]);

  // Render markdown to HTML
  const renderedHTML = useMemo(() => {
    if (!markdownContent) return '';
    
    try {
      const rawHTML = marked.parse(markdownContent) as string;
      // Sanitize the HTML output for security
      const sanitized = DOMPurify.sanitize(rawHTML, {
        ADD_ATTR: ['target', 'rel', 'class', 'id'],
        ADD_TAGS: ['span'],
        ALLOW_DATA_ATTR: false,
      });
      return sanitized;
    } catch (error) {
      console.error('Failed to render markdown:', error);
      return '<p>Error rendering markdown content</p>';
    }
  }, [markdownContent]);

  // Copy markdown source to clipboard
  const handleCopySource = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(markdownContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy markdown:', error);
    }
  }, [markdownContent]);

  // Scroll to heading
  const scrollToHeading = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  if (loading) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading markdown content...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={className}
      style={style}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Markdown Viewer Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'grey.50',
        }}
      >
        {/* Left side - Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
          >
            Markdown • {markdownContent.split('\n').length} lines • {markdownContent.length} chars
          </Typography>
        </Box>

        {/* Right side - Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: 'action.selected',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <Tooltip title="Preview">
              <ToggleButton value="preview">
                <PreviewIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Source">
              <ToggleButton value="source">
                <CodeIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Split View">
              <ToggleButton value="split">
                Split
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          {tableOfContents.length > 0 && (
            <Tooltip title="Table of Contents">
              <ToggleButton
                value="toc"
                selected={showToc}
                onChange={() => setShowToc(!showToc)}
                size="small"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    backgroundColor: 'action.selected',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <TocIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          )}

          {(viewMode === 'source' || viewMode === 'split') && (
            <Tooltip title={copied ? "Copied!" : "Copy Source"}>
              <ToggleButton
                value="copy"
                selected={false}
                onChange={handleCopySource}
                size="small"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </ToggleButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Table of Contents Sidebar */}
        {showToc && tableOfContents.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              width: 250,
              overflow: 'auto',
              borderRight: 1,
              borderColor: 'divider',
              p: 2,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Table of Contents
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {tableOfContents.map((item) => (
              <Box
                key={item.id}
                onClick={() => scrollToHeading(item.id)}
                sx={{
                  pl: (item.level - 1) * 2,
                  py: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: item.level <= 2 ? '0.875rem' : '0.75rem',
                    fontWeight: item.level === 1 ? 600 : 400,
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: viewMode === 'split' ? 'flex' : 'block',
            overflow: 'hidden',
          }}
        >
          {/* Source View */}
          {(viewMode === 'source' || viewMode === 'split') && (
            <Paper
              variant="outlined"
              sx={{
                flex: viewMode === 'split' ? 1 : undefined,
                m: 1,
                height: viewMode === 'split' ? 'calc(100% - 16px)' : 'calc(100% - 16px)',
                overflow: 'auto',
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  padding: 2,
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: 'text.primary',
                }}
              >
                <code>{markdownContent}</code>
              </Box>
            </Paper>
          )}

          {/* Preview View */}
          {(viewMode === 'preview' || viewMode === 'split') && (
            <Paper
              variant="outlined"
              sx={{
                flex: viewMode === 'split' ? 1 : undefined,
                m: 1,
                height: 'calc(100% - 16px)',
                overflow: 'auto',
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    marginTop: '1.5em',
                    marginBottom: '0.5em',
                    fontWeight: 600,
                    lineHeight: 1.25,
                  },
                  '& h1': { fontSize: '2em', borderBottom: '1px solid #e1e4e8', paddingBottom: '0.3em' },
                  '& h2': { fontSize: '1.5em', borderBottom: '1px solid #e1e4e8', paddingBottom: '0.3em' },
                  '& h3': { fontSize: '1.25em' },
                  '& h4': { fontSize: '1em' },
                  '& h5': { fontSize: '0.875em' },
                  '& h6': { fontSize: '0.85em', color: '#6a737d' },
                  '& p': { marginBottom: '16px' },
                  '& ul, & ol': { paddingLeft: '2em', marginBottom: '16px' },
                  '& li': { marginBottom: '4px' },
                  '& blockquote': {
                    margin: '0 0 16px 0',
                    padding: '0 1em',
                    color: '#6a737d',
                    borderLeft: '4px solid #dfe2e5',
                  },
                  '& code': {
                    padding: '0.2em 0.4em',
                    margin: 0,
                    fontSize: '85%',
                    backgroundColor: 'rgba(27,31,35,0.05)',
                    borderRadius: '3px',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  },
                  '& pre': {
                    padding: '16px',
                    overflow: 'auto',
                    fontSize: '85%',
                    lineHeight: 1.45,
                    backgroundColor: '#f6f8fa',
                    borderRadius: '6px',
                    marginBottom: '16px',
                  },
                  '& pre code': {
                    padding: 0,
                    margin: 0,
                    backgroundColor: 'transparent',
                    fontSize: '100%',
                  },
                  '& table': {
                    borderSpacing: 0,
                    borderCollapse: 'collapse',
                    marginBottom: '16px',
                    width: '100%',
                  },
                  '& table th, & table td': {
                    padding: '6px 13px',
                    border: '1px solid #dfe2e5',
                  },
                  '& table th': {
                    fontWeight: 600,
                    backgroundColor: '#f6f8fa',
                  },
                  '& table tr': {
                    backgroundColor: 'white',
                    borderTop: '1px solid #c6cbd1',
                  },
                  '& table tr:nth-of-type(even)': {
                    backgroundColor: '#f6f8fa',
                  },
                  '& hr': {
                    height: '4px',
                    padding: 0,
                    margin: '24px 0',
                    backgroundColor: '#e1e4e8',
                    border: 0,
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                  },
                  '& a': {
                    color: '#0366d6',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: renderedHTML }}
              />
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MarkdownViewer;